import * as Sentry from '@sentry/react'
import { Download } from 'lucide-react'
import { useState } from 'react'
import { useAtomValue } from 'jotai'
import { Button } from '@src/components/ui/button'
import apiClient from '@app/config/axios.config'
import { toast } from '@app/components/ui/Toast/toast'
import { logDataDogAction } from '@app/utils/logger'
import { DATADOG_ACTIONS } from '@app/app-constants'
import { pollForReport } from '@app/utils/pollForReport'
import { clientNameAtom } from '@app/state/clientsAtom'
import { userAtom } from '@app/hooks/useUserProfile'
import type { ProfileRiskRequest } from '../types/riskProfile.types'

type ExportAssetsCsvButtonProps = {
  request: ProfileRiskRequest | null
}

export const ExportAssetsCsvButton = ({ request }: ExportAssetsCsvButtonProps) => {
  const [isExporting, setIsExporting] = useState(false)
  const clientName = useAtomValue(clientNameAtom)
  const { data: user } = useAtomValue(userAtom)

  const handleExport = async () => {
    if (!request || !clientName) return

    const report_id = `${user?.user_uuid}-${Date.now()}`

    setIsExporting(true)

    toast({
      description: 'Generating CSV export. We will notify you when the report is ready.',
      type: 'info',
    })

    try {
      await apiClient.post(
        `/report/export/${clientName}/risk-profile-assets-csv/${report_id}`,
        request,
      )

      pollForReport(report_id, 'risk-profile-assets.csv')

      logDataDogAction(DATADOG_ACTIONS.EXPORT_REPORT, {
        type: 'risk-profile-assets',
        format: 'csv',
      })
    } catch (e) {
      Sentry.captureException(e)
      toast({
        description:
          'There was an error generating the export. Please notify your Urban Intelligence contact or submit feedback.',
        type: 'error',
      })
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      className="shrink-0 gap-1.5 px-2 text-xs"
      onClick={handleExport}
      disabled={isExporting || !request}
    >
      Export
      <Download className="size-3" />
    </Button>
  )
}
